import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { v4 as uuidv4 } from 'uuid';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("L'URL et la clé anonyme de Supabase doivent être définies dans lib/supabaseClient.ts");
}

// ============================================================================
// AUDIT CONTEXT
// ============================================================================
//
// Le trigger log_audit() lit le contexte réseau dans request.headers, propre à
// chaque requête PostgREST : IP (x-forwarded-for, posé par la passerelle),
// user-agent, et les en-têtes ci-dessous. Rien n'est posé en session : l'ancien
// set_audit_context() écrivait des GUC sur une connexion mutualisée, qui se
// retrouvaient sur les lignes d'autres utilisateurs, et appelait
// auth.getUser() avant chaque écriture.

// Identifiant d'onglet : regroupe les actions d'une même session de travail.
const AUDIT_SESSION_ID = uuidv4();

const requestUrl = (input: RequestInfo | URL): string =>
  typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

// Uniquement vers PostgREST (/rest/v1/) : la passerelle REST renvoie les
// en-têtes demandés dans Access-Control-Allow-Headers, mais les fonctions edge
// répondent au preflight avec une liste fixe (_shared/cors.ts) — un en-tête
// inconnu y ferait bloquer l'appel par le navigateur.
export const auditFetch: typeof fetch = (input, init) => {
  if (!requestUrl(input).includes('/rest/v1/')) {
    return fetch(input, init);
  }
  const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
  headers.set('x-audit-source', 'webapp');
  headers.set('x-audit-session', AUDIT_SESSION_ID);
  if (!headers.has('x-request-id')) {
    headers.set('x-request-id', uuidv4());
  }
  return fetch(input, { ...init, headers });
};

export const supabase: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
  global: {
    fetch: auditFetch,
  },
});
